import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { RouterConstant } from "../../routers/index";
import LoginHeader from "../../components/login-header";
import LoginForm from "../../components/login-form";
import { LoginService } from "../../services/login/login.services";
import { useUserStore } from "../../utils/userStore";
import { useLayoutContext } from "../../layouts/general-layout";
import { clientSideLogout } from "../../utils/authUtils";
import { gamificationStore } from "../../utils/gamificationStore";
import { getRequiredRoute } from "../../utils/navigationUtils";
import { UserType } from "../../types/userType";
import "./login.scss";
 
export default function Login() {
  const { ROUTERS_PATH } = RouterConstant;
  const navigate = useNavigate();
  const { t } = useLayoutContext();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [errors, setErrors] = useState<{ email?: string; general?: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false); // Add loading state
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get("session_expired") === "true";
  const fromAdminLogin = location.state?.fromAdminLogin || false;  
  const expectedUserTypes: UserType[] | undefined = location.state?.expectedUserTypes;
  const expectedUserTypeParam = expectedUserTypes ? expectedUserTypes.join(",") : undefined;
  
  const loginService = new LoginService();
  const { setUserInfo, setAttributes, resetUser, isAdmin, setJustLoggedIn, setLoggedIn, setAdminFlow } = useUserStore.getState();

  useEffect(() => {
    resetUser();    
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clientSideLogout();
    setErrors({});
    setIsLoading(true);

    try {
      const response: any = await loginService.login(email, password, expectedUserTypeParam);
      if (response.success && response.user) {

        setUserInfo({
          userId: response.user.id,
          first_name: response.user.first_name,
          last_name: response.user.last_name,
          email: response.user.email,
          phone: response.user.phone,
          is_access: response.user.is_access,
          org_id: response.user.org_id,
          org_type: response.user.org_type,
        });

        const loginCount = response.user.attributes?.find((attr: any) => attr.att_name === 'login_count')?.att_value || 0;
        let shouldShowVideo = false;
          if (loginCount <= 1) {
            shouldShowVideo = true;
          } 
          else if (loginCount == 2) {            
            shouldShowVideo = true;
        }

        setAttributes(response.user.attributes || []);
        setJustLoggedIn(true);
        setLoggedIn(true);
        setAdminFlow(fromAdminLogin);

        if (response.gamification_state) {
          gamificationStore.getState().setGamificationState({
            totalPoints: response.gamification_state.total_points,
            levelKey: response.gamification_state.level_key,
            levelLabel: response.gamification_state.level_label,
            levelIcon: response.gamification_state.level_icon || '',
            classMedian: response.gamification_state.class_median || 0,
            currentStreak: response.gamification_state.current_streak,
            longestStreak: response.gamification_state.longest_streak,
            lastActivityDate: response.gamification_state.last_activity_date,
          }, true);
        }

   
        const defaultRoute = getRequiredRoute(useUserStore.getState());
        const redirectParam = searchParams.get("redirect");
        const isValidInternalRedirect =
          redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//");

        const nextRoute =
          isValidInternalRedirect && defaultRoute === ROUTERS_PATH.USER_MAIN_DASHBOARD
            ? redirectParam
            : defaultRoute;

        navigate(nextRoute, { state: { fromLogin: true } });

      } else {
        setErrors({ general: t("loginFailed") });
      }
    } catch (err: any) {
      const codeToMessage: Record<string, string> = {
        invalid_credentials: t("invalidCredentials"),
        not_associated_with_org: t("notAssociatedWithOrg"),
        server_unavailable: t("serverUnavailable"),
        unauthorized: t("unauthorized"),
        login_failed: t("loginFailed"),
      };
    
      const errorMessage = codeToMessage[err.code] || t("loginFailed");
    
      if (err.code === "invalid_email_format") {
        setErrors({ email: t("invalidEmail") });
      } else {
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(ROUTERS_PATH.USERREGISTRATIONINITIAL);
  };

  return (
    <div className="full-head-template login-page custom-login-page">
      <LoginHeader handleBackClick={handleBackClick} title={t("loginWithEmailAndPassword")} subtitle={t("mustFillDetails")} noImg />
      <LoginForm
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        errors={errors}
        setErrors={setErrors}
        handleLogin={handleLogin}
        isLoading={isLoading}
        sessionExpired={sessionExpired}
      />
    </div>
  );
}
